/**
 * KayitIstegiModal
 * ==============
 * Landing / login / register'dan açılan kayıt isteği başvuru formu.
 * Focused Minimal Onboarding pattern (DESIGN_PATTERN.md) — slate palette.
 *
 * Araştırmadan çıkan best practices:
 *  - Max 5 alan (conversion %40-60 daha yüksek)
 *  - Kolay alanla başla (commitment psychology)
 *  - Outcome-focused headline
 */

import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useRouter } from 'next/router';
import axios from 'axios';
import { isValidTurkishPhone } from '@/shared/utils/telefonGecerli';
import { useBodyScrollLockEffect } from '@/application/hooks/useBodyScrollLock';
import { trackSignupRequestSubmit, trackSignupRequestError, trackSignupRequestModalClose, trackSignupRequestModalOpen } from '@/shared/utils/trackFunnel';
import { useAuth } from '@/application/contexts/AuthContext';
import { getAttribution } from '@/shared/utils/attribution';
import { suggestEmailFix } from '@/shared/utils/emailTypoGuard';
import { ROL_GRUPLARI } from './rolGruplari';
import { basvuruBelirteciSakla } from './BasvuruTamamlaKart';
import { istenenPaketiSakla } from '@/shared/utils/paketYonlendirme';
import {
  XMarkIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

interface FormState {
  email: string;
  isim: string;
  kurum: string;
  rol: string;
  calisanSayisi: string;
  firmaSayisi: string;
  telefon: string;
}

interface MessageState {
  kind: 'error' | 'info';
  title: string;
  text: string;
  contact?: boolean;
}

interface KayitIstegiModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Fiyat sayfasında "Satın al" ile seçilen ücretli paket; kod başvuruya kaydedilir, kayıt sonrası ödeme o paketle açılır */
  paket?: { kod: string; ad: string } | null;
  kaynakSayfa?: string | null;
}

interface DemoTalepResponse {
  code: string;
  id?: string;
  leadSinifi?: string;
  message?: string;
  tamamlaToken?: string;
}

interface AxiosErrorLike {
  response?: {
    data?: {
      code?: string;
      message?: string;
    };
  };
}

function isAxiosErrorLike(err: unknown): err is AxiosErrorLike {
  return typeof err === 'object' && err !== null && 'response' in err;
}

const CALISAN_OPTIONS = [
  { value: '', label: 'Çalışan sayısı (opsiyonel)' },
  { value: '1-10', label: '1-10 çalışan' },
  { value: '11-50', label: '11-50 çalışan' },
  { value: '51-200', label: '51-200 çalışan' },
  { value: '200+', label: '200+ çalışan' }
];

const FIRMA_OPTIONS = [
  { value: '', label: 'Hizmet firma sayısı (OSGB ise)' },
  { value: '1-10', label: '1-10 firma' },
  { value: '11-50', label: '11-50 firma' },
  { value: '51-100', label: '51-100 firma' },
  { value: '100+', label: '100+ firma' }
];

export default function KayitIstegiModal({ isOpen, onClose, paket = null, kaynakSayfa = null }: KayitIstegiModalProps) {
  const router = useRouter();
  const { googleLogin } = useAuth();
  const [form, setForm] = useState<FormState>({
    email: '',
    isim: '',
    kurum: '',
    rol: '',
    calisanSayisi: '',
    firmaSayisi: '',
    telefon: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const submitInFlightRef = useRef(false); // senkron çift-tık kilidi (submitting state re-render'dan önce ikinci tıkı kaçırır)
  const [submitted, setSubmitted] = useState(false);
  // message: { kind: 'error' | 'info', title: string, text: string } — null yoksa
  const [message, setMessage] = useState<MessageState | null>(null);
  // terminal: true ise form gizlenir (ALREADY_APPROVED, ACCOUNT_EXISTS vb.)
  const [terminal, setTerminal] = useState(false);
  // Inline field errors — onBlur validation sonucu
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  // typo-guard: "gmail.com mı?" düzeltme önerisi (lead kaybı önler — gmmail vakası)
  const [emailSuggestion, setEmailSuggestion] = useState<string | null>(null);
  const [closeConfirm, setCloseConfirm] = useState(false); // dolu-formda kazara kapanma koruması
  // Google ile başvur: Google'a gider, dönüşte /account-required'daki tek tık başvuruya düşer (hq 2 Kas)
  const [googleBusy, setGoogleBusy] = useState(false);
  const googleInFlightRef = useRef(false);

  const API_BASE = `${process.env.NEXT_PUBLIC_API_URL || 'https://api.riskmatik.com'}/api`;

  // Body scroll lock — declarative hook (rules-of-hooks safe)
  useBodyScrollLockEffect(isOpen);

  // GA funnel — açılış modalın kendisinde ölçülür (her açan sayfa için tek yer; form açılış → gönderim oranı)
  useEffect(() => {
    if (isOpen) trackSignupRequestModalOpen(kaynakSayfa ?? undefined);
  }, [isOpen, kaynakSayfa]);

  // ESC tuşu ile kapat
  // ESC ile kapanma — proje konvansiyonu (22 modal). HİZA korunur; kazara kayıp,
  // handleClose içindeki "dolu-formda confirm" ile önlenir (Ali 2026-06-26).
  // Ref: her renderda güncel handleClose'i tutar; ESC stale closure (boş form) çağırmaz.
  const handleCloseRef = useRef<() => void>(() => {});
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) handleCloseRef.current();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, submitting]);

  // Mesaj kutusu formun üstünde; telefonda gönder düğmesi en altta kaldığı için yeni mesaj görünür alana kaydırılır
  const messageRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (message) messageRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [message]);
  const setError = (text: string, opts?: Partial<MessageState>) => setMessage({ kind: 'error', title: 'Hata', text, ...opts });
  const setInfo = (title: string, text: string, opts?: Partial<MessageState>) => setMessage({ kind: 'info', title, text, ...opts });

  // Turkish phone: rakamları çıkar, 10 hane (5xxxxxxxxx) veya 11 hane (0 + 10 hane) kabul
  const formatPhone = (val: string): string => {
    const digits = val.replace(/\D/g, '').slice(0, 11);
    if (digits.length === 0) return '';
    // '0' ile başlıyorsa kaldır (normalize)
    const base = digits.startsWith('0') ? digits.slice(1) : digits;
    const d = base.slice(0, 10);
    if (d.length <= 3) return `0 ${d}`;
    if (d.length <= 6) return `0 ${d.slice(0, 3)} ${d.slice(3)}`;
    if (d.length <= 8) return `0 ${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}`;
    return `0 ${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6, 8)} ${d.slice(8, 10)}`;
  };


  const handleChange = (field: keyof FormState, value: string) => {
    const next = field === 'telefon' ? formatPhone(value) : value;
    setForm(prev => ({ ...prev, [field]: next }));
    // Aktif alanda yazmaya başlarsa inline error'ı temizle
    if (fieldErrors[field]) setFieldErrors(prev => ({ ...prev, [field]: '' }));
    if (field === 'email' && emailSuggestion) setEmailSuggestion(null);
    if (message) setMessage(null);
  };

  // onBlur — kutudan çıkınca anında kontrol
  const handleBlur = (field: keyof FormState) => {
    const val = form[field];
    if (field === 'email') {
      if (val && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
        setFieldErrors(prev => ({ ...prev, email: 'Geçerli bir e-posta girin' }));
      } else {
        // Geçerli format → domain typo önerisi kontrol et (gmmail→gmail)
        setEmailSuggestion(suggestEmailFix(val));
      }
    } else if (field === 'telefon') {
      if (val && !isValidTurkishPhone(val)) {
        setFieldErrors(prev => ({ ...prev, telefon: 'Geçersiz numara — örnek: 0 532 123 45 67' }));
      }
    }
  };

  const validate = (): boolean => {
    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setError('Geçerli bir e-posta girin');
      return false;
    }
    // typo-guard: submit anında domain typo varsa BLOKLA — onay maili yanlış adrese gitmesin (gmmail vakası)
    const emailFix = suggestEmailFix(form.email);
    if (emailFix) {
      setEmailSuggestion(emailFix);
      setError(`E-posta adresini kontrol edin — "${emailFix}" mı demek istediniz? Onay maili bu adrese gönderilir.`);
      return false;
    }
    if (!form.isim.trim()) { setError('Ad Soyad gerekli'); return false; }
    // Rol / kurum / telefon isteğe bağlı (hq 2 Kas); telefon yazıldıysa geçerli olmalı
    if (form.telefon.trim() && !isValidTurkishPhone(form.telefon)) {
      setError('Telefon numarası geçersiz. Örnek: 0 532 123 45 67');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Stale onBlur fieldError'ları + mesajları temizle, submit anında flash olmasın
    setFieldErrors({});
    setMessage(null);
    if (!validate()) {
      trackSignupRequestError('validation');
      return;
    }

    if (submitInFlightRef.current) return;
    submitInFlightRef.current = true;
    setSubmitting(true);

    try {
      // Trafik kaynağı (first-touch attribution — sessionStorage SSoT)
      const { referrer, utmSource, utmMedium, utmCampaign } = getAttribution();
      const payload = {
        ...form,
        rol: form.rol || undefined,
        kurum: form.kurum.trim() || (form.rol === 'BIREYSEL_UZMAN' ? 'Serbest Çalışan' : ''),
        // Trafik kaynağı — DemoTalep'e kaydedilir (nereden geldi)
        referrer, utmSource, utmMedium, utmCampaign,
        // Seçilen ücretli paketin kodu (arka uç satıştaki paketlerle doğrular) + geldiği sayfa
        istenenPaket: paket?.kod ?? null,
        kaynakSayfa: kaynakSayfa                       // "fiyatlandirma"|"register"|"login"|"landing"|"account-required"|null
      };
      const { data } = await axios.post<DemoTalepResponse>(`${API_BASE}/demo-talep/basvur`, payload);

      if (data.code === 'SUCCESS') {
        if (data.tamamlaToken) basvuruBelirteciSakla(data.tamamlaToken);
        const refNo = data.id ? `RM-${String(data.id).split('-')[0].toUpperCase()}` : '';
        trackSignupRequestSubmit({ rol: form.rol, leadClass: data.leadSinifi || null });
        // onClose ÇAĞIRMA — modal kapanış animasyonu + redirect arası ~300ms boşluk
        // yaratıyordu. Route değişince fiyatlandırma sayfası zaten unmount → modal da
        // unmount (cleanup gereksiz). Submit success → direkt navigate, sıfır boşluk.
        router.push(`/kayit-talep-alindi${refNo ? `?ref=${refNo}` : ''}`);
        return;
      }
      if (data.code === 'ALREADY_PENDING') {
        setInfo('Başvurunuz İnceleniyor', 'Bu e-posta ile bekleyen başvurunuz var. 24 saat içinde dönüş yapılacak — e-posta kutunuzu (ve spam/gereksiz klasörünüzü) kontrol edin.', { contact: true });
      } else if (data.code === 'ALREADY_APPROVED') {
        // Terminal state — form gizle, net eylem göster
        setInfo('Başvurunuz Onaylandı', 'Onay maili gönderildi. Email kutunuzun yanı sıra spam/gereksiz klasörünüzü de kontrol edin; oradaki bağlantıdan hesabınızı aktifleştirin.');
        setTerminal(true);
      } else {
        setError(data.message || 'Beklenmeyen yanıt.');
      }
    } catch (err: unknown) {
      const code = isAxiosErrorLike(err) ? err.response?.data?.code : undefined;
      if (code === 'ALREADY_REJECTED') {
        setInfo('Önceki Başvurunuz', 'Bu e-posta ile önceki başvurunuz değerlendirildi. Tekrar değerlendirilmesini isterseniz bize kısaca durumunuzu yazın, size dönelim.', { contact: true });
      } else if (code === 'ACCOUNT_EXISTS') {
        // Terminal — giriş yönlendirmesi
        setInfo('Hesabınız Mevcut', 'Bu e-posta ile kayıtlı bir hesabınız var. Giriş yaparak devam edin.');
        setTerminal(true);
      } else if (code === 'EMAIL_NOT_ELIGIBLE') {
        setError('Bu e-posta ile başvuru yapılamıyor.');
      } else {
        const errMsg = isAxiosErrorLike(err) ? err.response?.data?.message : undefined;
        setError(errMsg || 'Bir hata oluştu. Tekrar deneyin.');
      }
    } finally {
      submitInFlightRef.current = false;
      setSubmitting(false);
    }
  };

  const handleGoogle = async () => {
    if (googleInFlightRef.current) return;
    googleInFlightRef.current = true;
    setGoogleBusy(true);
    setMessage(null);
    try {
      // Google'a gidip dönerken seçili plan kaybolsun istenmez: önce sekme belleğine yaz (plansız açılışta eski seçim silinir)
      istenenPaketiSakla(paket?.kod ?? null);
      await googleLogin(); // sayfa Google'a gider; dönüş /auth/callback → /account-required
    } catch {
      setError('Google ile bağlantı kurulamadı. Tekrar deneyin ya da formu doldurun.');
      googleInFlightRef.current = false;
      setGoogleBusy(false);
    }
  };

  // Gerçek kapanış — abandonment track + formu sıfırla + onClose.
  const forceClose = () => {
    if (!submitted && !terminal) {
      // GA funnel — submit yapmadan kapattı mı (abandonment)
      const filledFields = Object.entries(form).filter(([, v]) => v && v.trim()).length;
      trackSignupRequestModalClose({ filledFields });
    }
    setForm({
      email: '', isim: '', kurum: '', rol: '',
      calisanSayisi: '', firmaSayisi: '', telefon: ''
    });
    setSubmitted(false);
    setMessage(null);
    setTerminal(false);
    setFieldErrors({});
    setEmailSuggestion(null);
    setCloseConfirm(false);
    onClose();
  };

  const handleClose = () => {
    if (submitting || googleBusy) return;
    // Dolu form + henüz gönderilmemiş → kazara kapanma koruması (Ali 2026-06-26: en önemli lead formu;
    // ESC/backdrop/X hepsi buradan geçer). Boş form veya success state → serbest kapanır (HİZA korunur).
    const filled = Object.entries(form).some(([, v]) => v && v.trim());
    if (!submitted && !terminal && filled) {
      setCloseConfirm(true);
      return;
    }
    forceClose();
  };
  handleCloseRef.current = handleClose;

  if (!isOpen) return null;
  // Portal hedefi yoksa (SSR) render etme — dynamic ssr:false ama defansif guard.
  if (typeof document === 'undefined') return null;

  const inputCls = "w-full px-3 py-2.5 text-base sm:text-sm text-slate-900 bg-white border border-slate-300 rounded-lg placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900 transition disabled:bg-slate-50 disabled:cursor-not-allowed";
  const labelCls = "block text-[11px] font-bold text-slate-900 uppercase tracking-[0.12em] mb-1.5";

  return createPortal(
    <div
      className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/50 backdrop-blur-sm flex items-start sm:items-center justify-center p-4"
      onClick={(e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden my-8">
        {/* Kapanış onayı — dolu formda kazara kapanma koruması (Ali 2026-06-26, en önemli lead formu) */}
        {closeConfirm && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/95 backdrop-blur-sm p-6">
            <div className="text-center max-w-xs">
              <h3 className="text-base font-bold text-slate-900">Başvurunuz kaybolacak</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                Girdiğiniz bilgiler silinecek. Çıkmak istediğinize emin misiniz?
              </p>
              <div className="flex gap-2 mt-5">
                <button
                  type="button"
                  onClick={() => setCloseConfirm(false)}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold text-slate-900 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition focus:outline-none focus:ring-2 focus:ring-slate-900"
                >
                  Vazgeç
                </button>
                <button
                  type="button"
                  onClick={forceClose}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-slate-900 rounded-lg hover:bg-slate-800 transition focus:outline-none focus:ring-2 focus:ring-slate-900 focus:ring-offset-2"
                >
                  Evet, çık
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Header */}
        <div className="relative px-6 sm:px-8 pt-7 pb-5 border-b border-slate-100">
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="absolute top-4 right-4 p-1.5 rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition disabled:opacity-50"
            aria-label="Kapat"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>

          <div className={`text-[11px] font-bold uppercase tracking-[0.14em] mb-2 ${submitted ? 'text-emerald-700' : 'text-slate-500'}`}>
            {submitted ? 'Alındı' : terminal ? 'Bilgi' : 'Kayıt İsteği'}
          </div>
          <h2 className="text-xl font-bold text-slate-900 leading-tight">
            {submitted
              ? 'Başvurunuz kaydedildi'
              : terminal
              ? 'Mevcut kayıt bulundu'
              : paket
              ? 'Önce hesabınızı açın'
              : 'Platformu denemek ister misiniz?'}
          </h2>
          {!submitted && !terminal && (
            /* 150 kredi hediye kancası — SSoT modal (Ali 2026-06-26: tüm CTA'larda görünür, drift yok) */
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-50 border border-amber-200 mt-2.5">
              <svg viewBox="0 0 24 24" className="w-6 h-6 flex-shrink-0" aria-hidden="true">
                <rect x="3.5" y="10.5" width="17" height="10" rx="1.5" fill="#fbbf24" />
                <rect x="2.5" y="7.5" width="19" height="3.8" rx="1" fill="#f59e0b" />
                <rect x="10.3" y="7.5" width="3.4" height="13" fill="#b45309" fillOpacity="0.9" />
                <ellipse cx="9" cy="5.6" rx="3" ry="2.2" fill="#f59e0b" transform="rotate(-20 9 5.6)" />
                <ellipse cx="15" cy="5.6" rx="3" ry="2.2" fill="#f59e0b" transform="rotate(20 15 5.6)" />
                <circle cx="12" cy="6.2" r="1.6" fill="#d97706" />
              </svg>
              <span className="text-[12.5px] font-bold text-amber-900">150 ücretsiz yapay zeka kredisi <span className="text-[15px] font-extrabold uppercase tracking-wide text-amber-700">hediye</span></span>
            </div>
          )}
          <p className="text-sm text-slate-600 mt-2 leading-relaxed">
            {submitted ? (
              '24 saat içinde e-posta adresinize dönüş yapacağız.'
            ) : terminal ? (
              'Bu e-posta için sistemde hâlihazırda bir kayıt mevcut.'
            ) : paket ? (
              <>
                Başvurunuz onaylanınca gelen bağlantıyla hesabınızı açın; ardından{' '}
                <strong className="text-slate-900">{paket.ad}</strong> planının ödeme sayfası açılır.
              </>
            ) : (
              'Başvuruları 24 saat içinde inceleyip davet kodu gönderiyoruz.'
            )}
          </p>
        </div>

        {/* Body */}
        <div className="px-6 sm:px-8 py-6">
          {terminal ? (
            /* TERMINAL STATE — ALREADY_APPROVED / ACCOUNT_EXISTS: form gizli, net eylem */
            <div className="space-y-5">
              {message && (
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                  <div className="text-[11px] font-bold text-slate-700 uppercase tracking-[0.12em] mb-1.5">
                    {message.title}
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed">{message.text}</p>
                </div>
              )}

              {message?.title?.includes('Hesabınız Mevcut') ? (
                <a
                  href="/auth/login"
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-slate-900 text-white text-sm font-semibold rounded-lg hover:bg-slate-800 active:bg-black transition"
                >
                  Giriş Sayfasına Git
                  <ArrowRightIcon className="w-4 h-4" />
                </a>
              ) : (
                <button
                  onClick={handleClose}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-slate-900 text-white text-sm font-semibold rounded-lg hover:bg-slate-800 active:bg-black transition"
                >
                  Tamam
                </button>
              )}

              <p className="text-[11px] text-slate-500 text-center leading-relaxed">
                Mail gelmediyse spam/gereksiz klasörünüzü kontrol edin. Yine ulaşmadıysa{' '}
                <a
                  href="mailto:destek@riskmatik.com"
                  className="text-slate-700 font-semibold underline decoration-slate-300 hover:text-slate-900 hover:decoration-slate-900"
                >
                  destek@riskmatik.com
                </a>{' '}
                adresine yazın, hesabınızı aktifleştirelim.
              </p>
            </div>
          ) : submitted ? (
            /* SUCCESS */
            <div className="space-y-5">
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <CheckCircleIcon className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-[11px] font-bold text-emerald-700 uppercase tracking-[0.12em] mb-1">
                      Başarılı
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed">
                      Ekibimiz başvurunuzu 24 saat içinde inceleyip bir e-posta gönderecek.
                      Onaylandığında hesabınızı aktifleştirebileceksiniz.
                    </p>
                  </div>
                </div>
              </div>

              <button
                onClick={handleClose}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-slate-900 text-white text-sm font-semibold rounded-lg hover:bg-slate-800 active:bg-black transition"
              >
                Kapat
              </button>
            </div>
          ) : (
            /* FORM */
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Neden başvuru — sektör pro filtreleme açıklaması */}
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                <p className="text-[11px] font-bold text-slate-700 uppercase tracking-[0.12em] mb-1">
                  Neden başvuru gerekli?
                </p>
                <p className="text-xs text-slate-600 leading-relaxed">
                  Riskmatik bir sahaya özel İSG yönetim platformu — A, B ve C sınıfı iş güvenliği uzmanları,
                  İSG teknikerleri ve OSGB&apos;ler için. Spam hesapları ayıklamak ve her kullanıcıya ekip içi
                  destek vermek için başvuruları manuel onaylıyoruz.
                  <strong className="text-slate-900"> 24 saat içinde davet kodu gönderilir.</strong>
                </p>
              </div>

              {message && (
                <div
                  ref={messageRef}
                  role={message.kind === 'error' ? 'alert' : 'status'}
                  className={`scroll-mt-4 p-3 rounded-lg border ${
                    message.kind === 'info'
                      ? 'bg-slate-50 border-slate-200'
                      : 'bg-red-50 border-red-200'
                  }`}
                >
                  <div
                    className={`text-[11px] font-bold uppercase tracking-[0.12em] mb-1 ${
                      message.kind === 'info' ? 'text-slate-700' : 'text-red-700'
                    }`}
                  >
                    {message.title}
                  </div>
                  <p
                    className={`text-sm leading-relaxed ${
                      message.kind === 'info' ? 'text-slate-700' : 'text-red-700'
                    }`}
                  >
                    {message.text}
                  </p>
                  {message.kind === 'info' && message.title.includes('Hesabınız Mevcut') && (
                    <a
                      href="/auth/login"
                      className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-slate-900 hover:underline"
                    >
                      Giriş sayfasına git →
                    </a>
                  )}
                  {message.contact && (
                    <a
                      href="mailto:destek@riskmatik.com"
                      className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-slate-900 hover:underline"
                    >
                      destek@riskmatik.com adresine yaz →
                    </a>
                  )}
                </div>
              )}

              <div className="space-y-3">
                <button
                  type="button"
                  onClick={handleGoogle}
                  disabled={googleBusy || submitting}
                  className="w-full min-h-[44px] inline-flex items-center justify-center gap-2 px-4 py-3 bg-white text-slate-900 text-sm font-semibold border border-slate-300 rounded-lg hover:bg-slate-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {googleBusy ? (
                    <>
                      <ArrowPathIcon className="w-4 h-4 animate-spin" />
                      {"Google'a yönlendiriliyor"}
                    </>
                  ) : (
                    'Google ile başvur'
                  )}
                </button>
                <div className="flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500">
                  <span className="flex-1 h-px bg-slate-200" />
                  veya formu doldurun
                  <span className="flex-1 h-px bg-slate-200" />
                </div>
              </div>

              <div>
                <label htmlFor="kayitistegimodal-e-posta" className={labelCls}>
                  E-posta <span className="text-red-600">*</span>
                </label>
                <input
                  id="kayitistegimodal-e-posta"
                  type="email"
                  value={form.email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('email', e.target.value)}
                  onBlur={() => handleBlur('email')}
                  placeholder="ornek@email.com"
                  className={`${inputCls} ${fieldErrors.email ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : ''}`}
                  disabled={submitting}
                  required
                />
                {fieldErrors.email ? (
                  <p className="text-[11px] text-red-600 mt-1.5 font-semibold">{fieldErrors.email}</p>
                ) : emailSuggestion ? (
                  <p className="text-[11px] mt-1.5 text-amber-700">
                    <button
                      type="button"
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => { e.preventDefault(); setForm(prev => ({ ...prev, email: emailSuggestion })); setEmailSuggestion(null); setMessage(null); }}
                      className="font-bold underline underline-offset-2 hover:text-amber-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 rounded"
                    >
                      {emailSuggestion}
                    </button>
                    {' '}mı demek istediniz? (tıkla düzelt)
                  </p>
                ) : (
                  <p className="text-[11px] text-slate-500 mt-1.5">Gmail, hotmail veya kurum maili — fark etmez</p>
                )}
              </div>

              <div>
                <label htmlFor="kayitistegimodal-ad-soyad" className={labelCls}>
                  Ad Soyad <span className="text-red-600">*</span>
                </label>
                <input
                  id="kayitistegimodal-ad-soyad"
                  type="text"
                  value={form.isim}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('isim', e.target.value)}
                  placeholder="Ahmet Yılmaz"
                  className={inputCls}
                  disabled={submitting}
                  required
                />
              </div>

              <p className="text-xs text-slate-600 leading-relaxed">
                Aşağıdakileri doldurursanız başvurunuz daha hızlı onaylanır.
              </p>

              <div>
                <label htmlFor="kayitistegimodal-rolunuz" className={labelCls}>
                  Rolünüz <span className="text-slate-500 normal-case tracking-normal font-normal">(isteğe bağlı)</span>
                </label>
                <select
                  id="kayitistegimodal-rolunuz"
                  value={form.rol}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleChange('rol', e.target.value)}
                  className={inputCls}
                  disabled={submitting}
                >
                  <option value="">Seçin</option>
                  {ROL_GRUPLARI.map(grup => (
                    <optgroup key={grup.label} label={grup.label}>
                      {grup.options.map(r => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="kayitistegimodal-kurum-osgb-adi" className={labelCls}>
                  Kurum / OSGB Adı <span className="text-slate-500 normal-case tracking-normal font-normal">(isteğe bağlı)</span>
                </label>
                <input
                  id="kayitistegimodal-kurum-osgb-adi"
                  type="text"
                  value={form.kurum}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('kurum', e.target.value)}
                  placeholder={form.rol === 'BIREYSEL_UZMAN' ? 'Serbest çalışıyorsanız boş bırakın' : 'ACME OSGB A.Ş.'}
                  className={inputCls}
                  disabled={submitting}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label htmlFor="kayitistegimodal-calisan-sayisi" className={labelCls}>Çalışan Sayısı</label>
                  <select
                    id="kayitistegimodal-calisan-sayisi"
                    value={form.calisanSayisi}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleChange('calisanSayisi', e.target.value)}
                    className={inputCls}
                    disabled={submitting}
                  >
                    {CALISAN_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>

                {['OSGB_YONETICISI', 'OSGB_ISG_UZMANI'].includes(form.rol) && (
                  <div>
                    <label htmlFor="kayitistegimodal-firma-sayisi" className={labelCls}>Firma Sayısı</label>
                    <select
                      id="kayitistegimodal-firma-sayisi"
                      value={form.firmaSayisi}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleChange('firmaSayisi', e.target.value)}
                      className={inputCls}
                      disabled={submitting}
                    >
                      {FIRMA_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div>
                <label htmlFor="kayitistegimodal-telefon" className={labelCls}>
                  Telefon <span className="text-slate-500 normal-case tracking-normal font-normal">(isteğe bağlı)</span>
                </label>
                <input
                  id="kayitistegimodal-telefon"
                  type="tel"
                  inputMode="numeric"
                  value={form.telefon}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('telefon', e.target.value)}
                  onBlur={() => handleBlur('telefon')}
                  placeholder="0 532 123 45 67"
                  maxLength={15}
                  className={`${inputCls} ${fieldErrors.telefon ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : ''}`}
                  disabled={submitting}
                />
                {fieldErrors.telefon ? (
                  <p className="text-[11px] text-red-600 mt-1.5 font-semibold">{fieldErrors.telefon}</p>
                ) : (
                  <p className="text-[11px] text-slate-500 mt-1.5">{"Başvurunuz için size WhatsApp'tan ulaşırız"}</p>
                )}
              </div>

              <div className="pt-2 space-y-3">
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-slate-900 text-white text-sm font-semibold rounded-lg hover:bg-slate-800 active:bg-black transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <ArrowPathIcon className="w-4 h-4 animate-spin" />
                      Gönderiliyor
                    </>
                  ) : (
                    <>
                      Başvuruyu Gönder
                      <ArrowRightIcon className="w-4 h-4" />
                    </>
                  )}
                </button>

                <p className="text-[11px] text-slate-500 text-center leading-relaxed">
                  Elinizde davet kodu varsa{' '}
                  <a href="/auth/register" className="text-slate-900 font-semibold underline decoration-slate-300 hover:decoration-slate-900">
                    buradan kayıt olun
                  </a>
                  .
                </p>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
